import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DocSection {
  id: string;
  title: string;
  count?: number;
  body: ReactNode;
}

const PARAM = 'section';

const headingOf = (id: string) => document.getElementById(`${id}-title`);

function IndexButton({ section: s, active, onChoose }: { section: DocSection; active: boolean; onChoose: (id: string) => void }) {
  return (
    <button type="button" className={active ? 'is-active' : undefined} aria-current={active ? 'true' : undefined} onClick={() => onChoose(s.id)}>
      {s.title}
      {s.count !== undefined && <span className="count">{s.count}</span>}
    </button>
  );
}

/** Up and Down move between the index buttons; choosing one (Enter, Space, click) is what opens it. */
function moveInIndex(event: KeyboardEvent<HTMLElement>) {
  const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
  if (!step || event.altKey || event.ctrlKey || event.metaKey) return;
  const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
  const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
  if (at < 0) return;
  event.preventDefault();
  buttons[(at + step + buttons.length) % buttons.length]?.focus();
}

function PrevNext({ prev, next, onChoose }: { prev?: DocSection; next?: DocSection; onChoose: (id: string) => void }) {
  if (!prev && !next) return null;
  return (
    <nav className="doc-section-pager" aria-label="Section pager">
      <button type="button" className="btn btn-secondary btn-sm" disabled={!prev} title={prev?.title} aria-label={prev ? `Previous: ${prev.title}` : 'Previous'} onClick={() => prev && onChoose(prev.id)}>
        Previous
      </button>
      <button type="button" className="btn btn-secondary btn-sm" disabled={!next} title={next?.title} aria-label={next ? `Next: ${next.title}` : 'Next'} onClick={() => next && onChoose(next.id)}>
        Next
      </button>
    </nav>
  );
}

/**
 * One section of the document at a time, with an index beside it (a select above it under 720px). The open
 * section is `?section=<id>` (replacing history); an unknown or absent id opens the first. Choosing a section
 * moves focus to its heading, so a screen reader lands on the new text. The index is a nav of buttons marked
 * `aria-current`, not a tablist, so it can sit inside a screen's own tabs. Ids must be unique on the page.
 */
export function DocumentSections({ sections }: { sections: DocSection[] }) {
  const [params, setParams] = useSearchParams();
  const asked = params.get(PARAM);
  const at = Math.max(0, sections.findIndex((s) => s.id === asked));
  const current = sections[at];
  const activeId = current?.id;
  // The section last shown, and whether the change came from the select (which keeps focus). Comparing ids, not
  // counting renders, keeps the first render and a StrictMode double effect from moving focus.
  const shown = useRef(activeId);
  const fromSelect = useRef(false);
  const selectId = useId();

  // A section opened by anything (index, pager, an Ask citation, Back) lands on its heading; the first render does not steal focus.
  useEffect(() => {
    if (shown.current === activeId) return;
    shown.current = activeId;
    if (fromSelect.current) fromSelect.current = false;
    else if (activeId) headingOf(activeId)?.focus();
  }, [activeId]);

  const choose = (id: string) => {
    if (id === activeId) {
      headingOf(id)?.focus();
      return;
    }
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set(PARAM, id);
      return next;
    }, { replace: true });
  };

  return (
    <div className="doc-sections">
      <nav className="doc-sections-nav" aria-label="Sections" onKeyDown={moveInIndex}>
        <ul>
          {sections.map((s) => (
            <li key={s.id}>
              <IndexButton section={s} active={s.id === activeId} onChoose={choose} />
            </li>
          ))}
        </ul>
      </nav>
      <div className="doc-sections-select">
        <label htmlFor={selectId}>Section</label>
        <select id={selectId} value={activeId ?? ''} onChange={(e) => {
            fromSelect.current = e.target.value !== activeId;
            choose(e.target.value);
          }}>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
              {s.count !== undefined ? ` (${s.count})` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="doc-sections-body">
        {current && (
          <section key={current.id} id={current.id} className="doc-section" aria-labelledby={`${current.id}-title`}>
            <h2 id={`${current.id}-title`} tabIndex={-1}>
              {current.title}
            </h2>
            {current.body}
            <PrevNext prev={sections[at - 1]} next={sections[at + 1]} onChoose={choose} />
          </section>
        )}
      </div>
    </div>
  );
}
